import {
  default as Button,
  default as CustomButton,
} from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import WorkflowStatus from "@/components/WorkflowStatus";
import { capitalizeText } from "@/lib/capitalizeText";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { normalizeEmptyStringsToNull } from "@/lib/normalizeEmptyStringsToNull";
import { fingerprintSchemaJson } from "@/lib/schemaFingerprint";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { TenantSchema, TenantSchemaProperty } from "@/utils/types/schemas";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "antd";
import { Printer, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useParams } from "react-router-dom";

const hasSchema = (
  json: TenantSchema["json"] | {}
): json is TenantSchemaProperty | Record<string, any> => {
  return (
    typeof json !== "string" &&
    json &&
    typeof json === "object" &&
    "schema" in json
  );
};

const hasXComponent = (
  property: any
): property is { "x-component": string; title: string; name: string } => {
  return (
    property &&
    typeof property === "object" &&
    "x-component" in property &&
    typeof property["x-component"] === "string" &&
    "title" in property &&
    typeof property["title"] === "string"
  );
};

const ModuleFormPage: React.FC = () => {
  const navigate = useAppNavigate();
  const queryClient = useQueryClient();

  const { schemaName = "", id } = useParams<{
    schemaName: string;
    id: string;
  }>();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName),
    enabled: !!schemaName,
  });

  const { data: moduleInfo, isLoading: moduleLoading } = useQuery({
    queryKey: ["dynamic-model", schemaName, id],
    queryFn: () => DynamicModuleService.getItemById(schemaName, id ?? ""),
    enabled: isEditMode && !!schemaName && !!id,
  });

  const schemaFingerprint = useMemo(
    () => fingerprintSchemaJson(schemaInfo?.json ?? schemaInfo),
    [schemaInfo]
  );

  const form = useMemo(
    () => createForm({}),
    // Recreate when schema route or designer JSON changes; createForm({}) does not reference those values.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional form instance reset
    [schemaName, schemaFingerprint]
  );

  const handleSubmit = async () => {
    try {
      await form.validate();
    } catch {
      notify.warning(
        "Validation",
        "Please fix the fields marked in red before saving."
      );
      return;
    }
    let modifiedValues = removeMetaFields(form.values);
    setIsSubmitting(true);

    try {
      if (schemaInfo?.json && hasSchema(schemaInfo.json)) {
        const properties = schemaInfo.json.schema?.properties ?? {};
        const valueKeys = Object.keys(modifiedValues);

        Object.values(properties).forEach(property => {
          if (
            hasXComponent(property) &&
            property["x-component"] === "CustomUpload"
          ) {
            if (valueKeys.includes(property["title"] ?? "")) {
              const value = modifiedValues[property["title"]];
              if (Array.isArray(value) && value.length > 0 && value[0]?.id) {
                modifiedValues = {
                  ...modifiedValues,
                  [property["title"]]: value[0].id,
                };
              }
            }
          } else if (
            hasXComponent(property) &&
            property["x-component"] === "CustomImageUpload"
          ) {
            if (valueKeys.includes(property["title"] ?? "")) {
              const value = modifiedValues[property["title"]];
              if (value && typeof value === "object" && "id" in value) {
                modifiedValues = {
                  ...modifiedValues,
                  [property["title"]]: value.id,
                };
              }
            }
          }
        });
      } else {
        console.warn("[ModuleFormPage] Schema not available or invalid type");
      }

      if (isEditMode && moduleInfo) {
        const originalData = removeMetaFields(moduleInfo);
        if (!checkFormChanges(modifiedValues, originalData, notify)) {
          setIsSubmitting(false);
          return;
        }
      }

      const payloadForApi = normalizeEmptyStringsToNull(
        modifiedValues as Record<string, unknown>
      ) as typeof modifiedValues;

      if (isEditMode) {
        const response = await DynamicModuleService.updateItem(
          schemaName,
          id!,
          payloadForApi
        );
        if (response) {
          await queryClient.invalidateQueries({
            queryKey: ["dynamic-model", schemaName],
          });
          await queryClient.invalidateQueries({
            queryKey: ["moduleData", schemaName],
          });
          navigate(navigationRoutes.MODULE(schemaName ?? ""));
        }
      } else {
        const response = await DynamicModuleService.createItem(
          schemaName,
          payloadForApi
        );
        if (response) {
          await queryClient.invalidateQueries({
            queryKey: ["dynamic-model", schemaName],
          });
          await queryClient.invalidateQueries({
            queryKey: ["moduleData", schemaName],
          });
          navigate(navigationRoutes.MODULE(schemaName ?? ""));
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    if (isEditMode && moduleInfo) {
      const values = removeMetaFields(moduleInfo);
      form.setInitialValues(values);
      form.setValues(values);
    }
  }, [isEditMode, moduleInfo, form]);

  if (isEditMode && moduleLoading) {
    return <LoadingSpinner />;
  }
  if (isEditMode && !moduleInfo) {
    return <NotFound entityName="Module" />;
  }
  if (schemaLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            <div className="flex w-full items-center justify-between gap-2.5">
              <span className="text-lg font-semibold">
                {isEditMode
                  ? `Edit ${capitalizeText(schemaName)}`
                  : `Add ${capitalizeText(schemaName)}`}
              </span>

              {isEditMode && (
                <WorkflowStatus schemaName={schemaName} recordId={id ?? ""} />
              )}
            </div>
          }
          extra={
            <div className="flex items-center gap-3">
              {isEditMode && (
                <Button
                  type="primary"
                  onClick={() =>
                    navigate(navigationRoutes.PRINT(schemaName ?? "", id ?? ""))
                  }
                >
                  <Printer size={16} />
                </Button>
              )}
              <CustomButton
                type="primary"
                icon={<Save size={16} />}
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting}
                size="middle"
                title={isEditMode ? "Save Record Changes" : "Save New Record"}
              >
                {isSubmitting
                  ? "Saving..."
                  : isEditMode
                    ? "Save Changes"
                    : "Save"}
              </CustomButton>
            </div>
          }
          onBack={() => window.history.back()}
        />
      }
    >
      <SchemaFormView
        key={`${schemaName}-${schemaFingerprint}`}
        form={form}
        schemaJson={schemaInfo?.json ?? schemaInfo}
      />
    </Card>
  );
};

export default ModuleFormPage;
